/**
 * Margin — the "don't run at a loss" guard, and the cost drill-down under it
 * (super_admin).
 *
 * One screen, three levels:
 *
 *   platform — per-brokerage revenue vs cost to serve, worst margin first.
 *   tenant   — that brokerage's DAILY rollups (click a row).
 *   events   — which event types drove its usage.
 *
 * The drill-down used to be a second top-level screen ("Cost dashboard") that
 * read the same `tenant_cost_rollups` and answered a strictly narrower
 * question: it showed cost with no revenue beside it, so it could never say
 * whether a brokerage was worth serving. Merged in here it keeps its real
 * value — the depth — under the view that asks the question.
 *
 * ⚠️ `usage_cost` is RETAIL, not cost: billing_engine's PER_USAGE plan bills
 * that exact number to the customer. The old screen's headline was
 * `AWS + usage`, which added what we pay to what we charge — a figure that
 * looked healthier the more we made. Nothing here sums those two.
 *
 * Cost to serve is AWS **plus** that tenant's share of the third-party
 * subscriptions (V080) — voice, SMS, email, e-sign.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { billingService } from "@re-platform/api-client";
import type {
  CostTimeseriesResponse,
  DailyRollup,
  EventTypeTrend,
  Granularity,
  MarginResponse,
} from "@re-platform/api-client";
import { Card } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  bodyRow,
  tableShell,
  TableScroll,
  Td,
  Th,
  theadRow,
} from "../../lib/tables";

type View =
  | { level: "platform" }
  | { level: "tenant"; tenantId: string; displayName: string }
  | { level: "events"; tenantId: string; displayName: string };

function money(v: string | null): string {
  if (v === null) return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : `$${n.toFixed(2)}`;
}

/** auto-fit, not a fixed track count — three fixed columns squeeze to slivers
 *  on a narrow window instead of wrapping. */
const summaryGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 16,
  marginBottom: 24,
};

export function Margin() {
  const [view, setView] = useState<View>({ level: "platform" });

  const title =
    view.level === "platform"
      ? "Margin"
      : view.level === "tenant"
        ? `${view.displayName} — daily costs`
        : `${view.displayName} — event trends`;

  return (
    <>
      <h1 style={{ marginTop: 0 }}>{title}</h1>

      {view.level !== "platform" && (
        <BackLink
          label={
            view.level === "events"
              ? `← ${view.displayName}`
              : "← All brokerages"
          }
          onClick={() =>
            setView(
              view.level === "events"
                ? {
                    level: "tenant",
                    tenantId: view.tenantId,
                    displayName: view.displayName,
                  }
                : { level: "platform" },
            )
          }
        />
      )}

      {view.level === "platform" && (
        <PlatformView
          onDrillTenant={(tenantId, displayName) =>
            setView({ level: "tenant", tenantId, displayName })
          }
        />
      )}
      {view.level === "tenant" && (
        <TenantView
          tenantId={view.tenantId}
          displayName={view.displayName}
          onDrillEvents={() =>
            setView({
              level: "events",
              tenantId: view.tenantId,
              displayName: view.displayName,
            })
          }
        />
      )}
      {view.level === "events" && (
        <EventsView tenantId={view.tenantId} displayName={view.displayName} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 1 — margin per brokerage
// ---------------------------------------------------------------------------

function PlatformView({
  onDrillTenant,
}: {
  onDrillTenant: (tenantId: string, displayName: string) => void;
}) {
  const [granularity, setGranularity] = useState<Granularity>("daily");

  const margin = useQuery<MarginResponse>({
    queryKey: ["billing-margin"],
    queryFn: () => billingService.getMargin(api),
    staleTime: 60_000,
    retry: false,
  });

  const series = useQuery<CostTimeseriesResponse>({
    queryKey: ["billing-timeseries", granularity],
    queryFn: () => billingService.getCostTimeseries(api, { granularity }),
    staleTime: 60_000,
    retry: false,
  });

  return (
    <>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        What we bill each brokerage vs. what it costs to serve them — AWS plus
        their share of the third-party subscriptions. Brokerages where cost eats
        more than{" "}
        {margin.data
          ? `${(Number(margin.data.risk_threshold) * 100).toFixed(0)}%`
          : "80%"}{" "}
        of revenue are flagged. Click one to see its daily costs.
      </p>

      {margin.data && (
        <div style={summaryGrid}>
          <Stat
            label="Revenue (billed)"
            value={money(margin.data.platform_revenue)}
          />
          <Stat label="AWS cost" value={money(margin.data.platform_aws_cost)} />
          <Stat
            label="Subscriptions"
            value={money(margin.data.platform_subscription_cost)}
          />
          <Stat
            label="Margin"
            value={money(margin.data.platform_margin)}
            danger={Number(margin.data.platform_margin) < 0}
          />
        </div>
      )}

      {margin.isError ? (
        <Card padding="lg" style={{ marginBottom: 32 }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            Could not load margin (super-admin only).
          </p>
        </Card>
      ) : (
        <Card padding="none" style={{ marginBottom: 32, overflow: "hidden" }}>
          <TableScroll>
            <table style={tableShell}>
              <thead>
                <tr style={theadRow}>
                  <Th>Brokerage</Th>
                  <Th>Plan</Th>
                  <Th numeric>Revenue</Th>
                  <Th numeric>AWS</Th>
                  <Th numeric>Subscriptions</Th>
                  <Th numeric>Cost to serve</Th>
                  <Th numeric>Margin</Th>
                  <Th numeric>Margin %</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {(margin.data?.rows ?? []).map((r) => {
                  const costToServe =
                    Number(r.aws_cost) + Number(r.subscription_cost);
                  return (
                    <tr
                      key={r.tenant_id}
                      onClick={() => onDrillTenant(r.tenant_id, r.display_name)}
                      style={{
                        ...bodyRow,
                        cursor: "pointer",
                        // The token is themed (Aurora's is a dark plum); the
                        // literal fallback this once carried would have been a
                        // near-white band on that theme.
                        background: r.at_risk
                          ? "var(--rp-color-danger-faint)"
                          : undefined,
                      }}
                    >
                      <Td bold>{r.display_name}</Td>
                      <Td muted>{r.plan_type ?? "— no plan"}</Td>
                      <Td numeric>{money(r.revenue)}</Td>
                      <Td numeric>{money(r.aws_cost)}</Td>
                      <Td numeric>{money(r.subscription_cost)}</Td>
                      <Td numeric>{money(costToServe.toFixed(2))}</Td>
                      <Td numeric bold>
                        <span
                          style={{
                            color:
                              Number(r.margin) < 0
                                ? "var(--rp-color-danger)"
                                : undefined,
                          }}
                        >
                          {money(r.margin)}
                        </span>
                      </Td>
                      <Td numeric>
                        {r.margin_pct === null
                          ? "—"
                          : `${Number(r.margin_pct).toFixed(0)}%`}
                      </Td>
                      <Td muted>›</Td>
                    </tr>
                  );
                })}
                {margin.data && margin.data.rows.length === 0 && (
                  <tr style={bodyRow}>
                    <Td muted>No cost data yet.</Td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ fontSize: 16, margin: 0 }}>Platform cost over time</h2>
        <div style={{ display: "flex", gap: 4 }}>
          {(["daily", "weekly", "monthly"] as Granularity[]).map((g) => (
            <button
              key={g}
              type="button"
              className={`rp-btn rp-btn--sm ${
                granularity === g ? "rp-btn--primary" : "rp-btn--secondary"
              }`}
              onClick={() => setGranularity(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <Card padding="none" style={{ overflow: "hidden" }}>
        <TableScroll>
          <table style={tableShell}>
            <thead>
              <tr style={theadRow}>
                <Th>Period</Th>
                <Th numeric>AWS (our cost)</Th>
                <Th numeric>Usage (retail)</Th>
              </tr>
            </thead>
            <tbody>
              {(series.data?.points ?? []).map((p) => (
                <tr key={p.period_start} style={bodyRow}>
                  <Td>{p.period_start}</Td>
                  <Td numeric>{money(p.total_aws)}</Td>
                  <Td numeric>{money(p.total_usage)}</Td>
                </tr>
              ))}
              {series.data && series.data.points.length === 0 && (
                <tr style={bodyRow}>
                  <Td muted>No cost data in this window.</Td>
                </tr>
              )}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 2 — one brokerage's daily rollups
// ---------------------------------------------------------------------------

function TenantView({
  tenantId,
  displayName,
  onDrillEvents,
}: {
  tenantId: string;
  displayName: string;
  onDrillEvents: () => void;
}) {
  const q = useQuery({
    queryKey: ["billing", "costs", tenantId],
    queryFn: () => billingService.getTenantCosts(api, tenantId),
    staleTime: 60_000,
    retry: false,
  });

  if (q.isLoading) return <Muted>Loading…</Muted>;
  if (q.isError) return <Muted>Could not load daily costs.</Muted>;
  const data = q.data!;

  const totalAws = data.daily.reduce(
    (s, r) => s + parseFloat(r.aws_attributed ?? "0"),
    0,
  );
  const totalUsage = data.daily.reduce(
    (s, r) => s + parseFloat(r.usage_cost ?? "0"),
    0,
  );

  return (
    <>
      {/* Two figures, deliberately NOT summed: the first is what this brokerage
          cost us, the second what its usage is worth at our retail card. Adding
          them was the old screen's headline and meant nothing. */}
      <div style={summaryGrid}>
        <Stat label="AWS cost (period)" value={`$${totalAws.toFixed(2)}`} />
        <Stat
          label="Usage at retail (period)"
          value={`$${totalUsage.toFixed(2)}`}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="rp-btn rp-btn--secondary rp-btn--sm"
          onClick={onDrillEvents}
        >
          View event-type trends →
        </button>
      </div>

      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginBottom: 8,
        }}
      >
        {data.since} → {data.until} · {data.daily.length} daily rows
      </p>

      <Card padding="none" style={{ overflow: "hidden" }}>
        <TableScroll>
          <table style={tableShell}>
            <thead>
              <tr style={theadRow}>
                <Th>Date</Th>
                <Th numeric>AWS (our cost)</Th>
                <Th numeric>Usage (retail)</Th>
              </tr>
            </thead>
            <tbody>
              {data.daily.length === 0 && (
                <tr style={bodyRow}>
                  <Td muted>
                    No daily rollups for {displayName} in this period.
                  </Td>
                </tr>
              )}
              {data.daily.map((row: DailyRollup) => (
                <tr key={row.rollup_date} style={bodyRow}>
                  <Td>{row.rollup_date}</Td>
                  <Td numeric>{money(row.aws_attributed)}</Td>
                  <Td numeric>{money(row.usage_cost)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 3 — what drove the usage
// ---------------------------------------------------------------------------

function EventsView({
  tenantId,
  displayName,
}: {
  tenantId: string;
  displayName: string;
}) {
  const q = useQuery({
    queryKey: ["billing", "costs", tenantId, "events"],
    queryFn: () => billingService.getTenantEventTrends(api, tenantId),
    staleTime: 60_000,
    retry: false,
  });

  if (q.isLoading) return <Muted>Loading…</Muted>;
  if (q.isError) return <Muted>Could not load event trends.</Muted>;
  const data = q.data!;

  const byType = new Map<string, EventTypeTrend[]>();
  for (const t of data.trends) {
    if (!byType.has(t.event_type)) byType.set(t.event_type, []);
    byType.get(t.event_type)!.push(t);
  }

  return (
    <>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        {displayName} · {data.since} → {data.until}
      </p>
      {byType.size === 0 && <Muted>No usage events in this period.</Muted>}
      {Array.from(byType.entries()).map(([eventType, rows]) => {
        const total = rows.reduce(
          (s, r) => s + parseFloat(r.total_quantity),
          0,
        );
        return (
          <div key={eventType} style={{ marginBottom: 24 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>
              {eventType}{" "}
              <span
                style={{ fontWeight: 400, color: "var(--rp-color-text-muted)" }}
              >
                ({total.toFixed(0)} total)
              </span>
            </h3>
            <Card padding="none" style={{ overflow: "hidden" }}>
              <TableScroll>
                <table style={tableShell}>
                  <thead>
                    <tr style={theadRow}>
                      <Th>Day</Th>
                      <Th numeric>Quantity</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.day} style={bodyRow}>
                        <Td>{r.day}</Td>
                        <Td numeric>
                          {parseFloat(r.total_quantity).toFixed(2)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </Card>
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Micro-components
// ---------------------------------------------------------------------------

function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        color: "var(--rp-color-primary)",
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      {label}
    </button>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
      {children}
    </p>
  );
}

function Stat({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--rp-color-text-muted)" }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: danger ? "var(--rp-color-danger)" : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}
